import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type PriceMover = {
  print_id: string;
  card_name: string;
  set_name: string;
  ink_color: string;
  image_url: string | null;
  current_price: number;
  prior_price: number;
  delta: number;
  pct: number;
  series: { t: number; price: number }[];
};

/**
 * Build weekly gainers & losers from price_history.
 * Returns up to `limit` of each list, sorted by absolute % change.
 * Falls back to current card_prints prices when no history exists.
 */
export function usePriceMovers(limit: number = 5) {
  return useQuery({
    queryKey: ['price-movers', limit],
    staleTime: 1000 * 60 * 30,
    queryFn: async (): Promise<{ gainers: PriceMover[]; losers: PriceMover[]; hasHistory: boolean }> => {
      const since = new Date(Date.now() - 1000 * 60 * 60 * 24 * 14).toISOString();

      const { data: history, error } = await supabase
        .from('price_history')
        .select('print_id, price_usd, recorded_at')
        .gte('recorded_at', since)
        .order('recorded_at', { ascending: true })
        .limit(5000);
      if (error) throw error;

      const grouped = new Map<string, { t: number; price: number }[]>();
      for (const row of history || []) {
        const arr = grouped.get(row.print_id) || [];
        arr.push({ t: new Date(row.recorded_at).getTime(), price: Number(row.price_usd) });
        grouped.set(row.print_id, arr);
      }

      // Build movers from anything with at least 2 datapoints across the week
      const weekAgo = Date.now() - 1000 * 60 * 60 * 24 * 7;
      const movers: Omit<PriceMover, 'card_name' | 'set_name' | 'ink_color' | 'image_url'>[] = [];

      for (const [printId, arr] of grouped) {
        if (arr.length < 2) continue;
        const current = arr[arr.length - 1].price;
        const priorEntry = [...arr].reverse().find(p => p.t <= weekAgo) || arr[0];
        const prior = priorEntry.price;
        if (!prior || !current) continue;
        const delta = current - prior;
        if (Math.abs(delta) < 0.01) continue;
        movers.push({
          print_id: printId,
          current_price: current,
          prior_price: prior,
          delta,
          pct: (delta / prior) * 100,
          series: arr,
        });
      }

      // Hydrate with print + card metadata
      let enriched: PriceMover[] = [];
      const printIds = movers.map(m => m.print_id);
      if (printIds.length) {
        const { data: prints } = await supabase
          .from('card_prints')
          .select('id, image_url, card_id')
          .in('id', printIds);
        const printMap = new Map((prints || []).map(p => [p.id, p]));
        const cardIds = [...new Set((prints || []).map(p => p.card_id))];
        const { data: cards } = cardIds.length
          ? await supabase.from('cards').select('id, name, set_name, ink_color').in('id', cardIds)
          : { data: [] as any[] };
        const cardMap = new Map((cards || []).map(c => [c.id, c]));

        enriched = movers
          .map(m => {
            const print = printMap.get(m.print_id);
            const card = print ? cardMap.get(print.card_id) : null;
            if (!card) return null;
            return {
              ...m,
              card_name: card.name,
              set_name: card.set_name,
              ink_color: card.ink_color,
              image_url: print?.image_url ?? null,
            } as PriceMover;
          })
          .filter(Boolean) as PriceMover[];
      }

      const hasHistory = enriched.length > 0;
      const gainers = [...enriched].filter(m => m.delta > 0).sort((a, b) => b.pct - a.pct).slice(0, limit);
      const losers = [...enriched].filter(m => m.delta < 0).sort((a, b) => a.pct - b.pct).slice(0, limit);

      return { gainers, losers, hasHistory };
    },
  });
}
